import React, { useCallback, useEffect, useState } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DeleteForever as DeleteForeverIcon,
  CleaningServices as PurgeIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import dataRetentionService, {
  ExpiredDataSummary,
  PurgeResult,
  UserErasurePreview,
  UserErasureResult,
} from '../services/dataRetentionService';
import { userService } from '../services/userService';
import { useConfirm } from '../contexts/ConfirmContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { UserListItem } from '../types/user';

/**
 * Adattörlés — az adatkezelési tájékoztatóban vállaltak érvényesítése.
 *
 * A képernyő MINDKÉT művelete visszafordíthatatlan, ezért mindkettő kétlépcsős:
 * előbb megmutatjuk, mi fog történni, és csak külön megerősítésre hajtjuk végre.
 */
const DataRetention: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();
  const { showSuccess } = useFeedback();

  // --- Lejárt naplók ---
  const [summary, setSummary] = useState<ExpiredDataSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<PurgeResult | null>(null);

  // --- Felhasználó adatainak törlése ---
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [preview, setPreview] = useState<UserErasurePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [eraseResult, setEraseResult] = useState<UserErasureResult | null>(null);

  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      setSummary(await dataRetentionService.getExpired());
    } catch (err) {
      setError(apiErrorMessage(err, 'Nem sikerült lekérdezni a lejárt adatokat.'));
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    userService
      .getUsers()
      .then((result) => setUsers(result.data))
      .catch((err) => setError(apiErrorMessage(err, 'Nem sikerült betölteni a felhasználókat.')));
  }, [loadSummary]);

  const handleSelectUser = async (user: UserListItem | null) => {
    setSelectedUser(user);
    setPreview(null);
    if (!user) return;

    setPreviewLoading(true);
    try {
      setPreview(await dataRetentionService.previewUserErasure(user.userId));
    } catch (err) {
      setError(apiErrorMessage(err, 'Nem sikerült összeállítani az előnézetet.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Az Autocomplete-en keresztüli VALÓDI felhasználóváltásnál a korábbi törlés-eredményt is
  // eltüntetjük — az egy MÁSIK felhasználóról szólt, itt csak megzavarná az adminot. A
  // `handleSelectUser` a törlés utáni előnézet-frissítéshez is fut (ugyanarra a felhasználóra),
  // ott ez a nullázás nem kell — ezért külön kezeljük, nem magában `handleSelectUser`-ben.
  const handleUserPicked = (user: UserListItem | null) => {
    setEraseResult(null);
    handleSelectUser(user);
  };

  const handlePurge = async () => {
    const confirmed = await confirm({
      title: 'Lejárt naplók törlése',
      message: (
        <>
          <strong>Ez a művelet nem vonható vissza.</strong> {summary?.total} napló-bejegyzés
          véglegesen törlődik.
          {'\n\n'}A jelenléti és pénzügyi adatokat ez a művelet nem érinti.
        </>
      ),
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    setPurging(true);
    setError(null);
    try {
      const result = await dataRetentionService.purgeExpired();
      // A darabszám szerinti bontást (purgeResult) TARTÓSAN a lapon hagyjuk — ez egy
      // visszafordíthatatlan törlés eredmény-összegzése, amit az adminnak látnia kell akkor is,
      // ha lemaradt a toastról. Az egyszerű megerősítés viszont toast, mint minden más sikeres
      // művelet a projektben.
      setPurgeResult(result);
      showSuccess('A lejárt naplók törölve.');
      await loadSummary();
    } catch (err) {
      setError(apiErrorMessage(err, 'A törlés nem sikerült.'));
    } finally {
      setPurging(false);
    }
  };

  const handleErase = async () => {
    if (!selectedUser) return;
    setErasing(true);
    setError(null);
    try {
      const result = await dataRetentionService.eraseUser(selectedUser.userId);
      // Ez is visszafordíthatatlan törlés, ugyanúgy, mint a napló-purge — ezért az
      // eredményt (result) TARTÓSAN, kézzel zárható Alerten is megjelenítjük, nem csak
      // toaston: az admin percekkel később is lássa, pontosan mi történt, akár lemaradt
      // a toastról. A toast emellett megmarad, hogy azonnali visszajelzés is legyen.
      setEraseResult(result);
      showSuccess(result.message);
      setEraseOpen(false);
      setConfirmName('');
      // Az előnézetet ÚJRATÖLTJÜK, hogy a képernyő a valóságot mutassa: a mezők
      // eltűnnek, és látszik, mi maradt meg névtelenül.
      await handleSelectUser(selectedUser);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'A törlés nem sikerült.'));
    } finally {
      setErasing(false);
    }
  };

  // A megerősítéshez BE KELL GÉPELNI a nevet. Egy „Biztos?" ablakra rá lehet kattintani
  // félrenézve is — ezt a lépést nem lehet véletlenül megtenni.
  const confirmMatches = confirmName.trim() === selectedUser?.name.trim();

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        🗑️ Adattörlés
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Az adatkezelési tájékoztatóban vállalt megőrzési idők érvényesítése. Az itt indított
        műveletek <strong>nem vonhatók vissza</strong>.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* ---------- 1. Lejárt naplók ---------- */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <PurgeIcon color="primary" />
          <Typography variant="h6">Lejárt naplóbejegyzések</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A megőrzési időn túli naplók. Ezek nem tartalmaznak jelenléti vagy pénzügyi adatot.
        </Typography>

        {summaryLoading ? (
          <CircularProgress size={24} />
        ) : summary ? (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Adatfajta</TableCell>
                    <TableCell>Megőrzési idő</TableCell>
                    <TableCell align="right">Lejárt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Audit napló</TableCell>
                    <TableCell>{summary.auditLogRetentionDays} nap</TableCell>
                    <TableCell align="right">{summary.auditLogs}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Alkalmazás-hibajelentés</TableCell>
                    <TableCell>{summary.clientErrorRetentionDays} nap</TableCell>
                    <TableCell align="right">{summary.clientErrors}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>SOS hozzáférési napló</TableCell>
                    <TableCell>{summary.sosAccessLogRetentionDays} nap</TableCell>
                    <TableCell align="right">{summary.sosAccessLogs}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Visszavont eszköz-azonosító</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell align="right">{summary.inactiveDeviceTokens}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                Összesen <strong>{summary.total}</strong> törölhető bejegyzés.
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="warning"
                startIcon={<DeleteForeverIcon />}
                disabled={summary.total === 0 || purging}
                onClick={handlePurge}
                fullWidth={isMobile}
              >
                Lejárt naplók törlése
              </Button>
            </Stack>

            {purgeResult && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Törölve összesen <strong>{purgeResult.total}</strong> bejegyzés:{' '}
                {purgeResult.auditLogsDeleted} audit, {purgeResult.clientErrorsDeleted}{' '}
                hibajelentés, {purgeResult.sosAccessLogsDeleted} SOS napló,{' '}
                {purgeResult.deviceTokensDeleted} eszköz-azonosító.
              </Alert>
            )}
          </>
        ) : null}
      </Paper>

      {/* ---------- 2. Felhasználó adatainak törlése ---------- */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <PersonOffIcon color="error" />
          <Typography variant="h6">Felhasználó adatainak törlése</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Törlési kérelem teljesítése. A személyes adatok véglegesen eltűnnek, a jelenléti és
          pénzügyi bejegyzések <strong>névtelenül megmaradnak</strong>, hogy az egyesület
          összesítései visszamenőleg ne változzanak meg.
        </Typography>

        {eraseResult && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setEraseResult(null)}>
            {/* A backend üzenetét (eraseResult.message) SAJÁT, önálló szövegcsomópontként adjuk ki
                — ugyanaz a szöveg, mint a toaston —, a részletező bontást pedig KÜLÖN sorban, hogy
                a kettő ne olvadjon egybe egyetlen szöveg-egyezéssé. */}
            <Typography component="div" variant="body2">
              {eraseResult.message}
            </Typography>
            <Typography component="div" variant="body2" color="text.secondary">
              {eraseResult.fieldsErased} mező törölve
              {eraseResult.profileImageDeleted ? ', profilkép törölve' : ''}
              {eraseResult.coachNotesDeleted > 0
                ? `, ${eraseResult.coachNotesDeleted} edzői jegyzet törölve`
                : ''}
              {eraseResult.privateLessonRequestsDeleted > 0
                ? `, ${eraseResult.privateLessonRequestsDeleted} magánóra-igénylés törölve`
                : ''}
              .
            </Typography>
          </Alert>
        )}

        <Autocomplete
          options={users}
          value={selectedUser}
          onChange={(_, value) => handleUserPicked(value)}
          getOptionLabel={(u) => `${u.name} (${u.email})`}
          isOptionEqualToValue={(a, b) => a.userId === b.userId}
          renderInput={(params) => <TextField {...params} label="Felhasználó" />}
          sx={{ maxWidth: 520, mb: 3 }}
        />

        {previewLoading && <CircularProgress size={24} />}

        {preview && !previewLoading && (
          <>
            {preview.alreadyErased && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Ennek a felhasználónak az adatait már törölték.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Ezek az adatok véglegesen törlődnek ({preview.fieldsToErase.length} mező):
            </Typography>
            <TableContainer sx={{ overflowX: 'auto', mb: 2 }}>
              <Table size="small">
                <TableBody>
                  {preview.fieldsToErase.map((field) => (
                    <TableRow key={field.field}>
                      <TableCell sx={{ width: '40%' }}>{field.field}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{field.currentValue}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {preview.hasProfileImage && <Chip size="small" label="Profilkép törlése" />}
              {preview.coachNotesToDelete > 0 && (
                <Chip size="small" label={`${preview.coachNotesToDelete} edzői jegyzet törlése`} />
              )}
              {/* [É4] A magánóra-igénylések is TÖRLŐDNEK (`DataRetentionController` erase) — az
                  előnézet ezt eddig elhallgatta, mert a service-típusból kimaradt a mező. */}
              {preview.privateLessonRequestsToDelete > 0 && (
                <Chip
                  size="small"
                  label={`${preview.privateLessonRequestsToDelete} magánóra-igénylés törlése`}
                />
              )}
            </Stack>

            {preview.retainedRecords.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Ezek névtelenül megmaradnak:
                </Typography>
                <TableContainer sx={{ overflowX: 'auto', mb: 2 }}>
                  <Table size="small">
                    <TableBody>
                      {preview.retainedRecords.map((record) => (
                        <TableRow key={record.record}>
                          <TableCell sx={{ width: '40%' }}>
                            {record.record} ({record.count} db)
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{record.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
              disabled={preview.fieldsToErase.length === 0}
              onClick={() => {
                setConfirmName('');
                setEraseOpen(true);
              }}
              fullWidth={isMobile}
            >
              Adatok végleges törlése
            </Button>
          </>
        )}
      </Paper>

      {/* ---------- Megerősítés: felhasználó adatai ---------- */}
      <Dialog
        open={eraseOpen}
        onClose={() => setEraseOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Felhasználó adatainak törlése</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Ez a művelet nem vonható vissza</AlertTitle>
            <strong>{selectedUser?.name}</strong> személyes adatai véglegesen törlődnek, és a fiók
            használhatatlanná válik.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A megerősítéshez írd be a felhasználó nevét:
          </Typography>
          <TextField
            fullWidth
            autoFocus
            label="Felhasználó neve"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={selectedUser?.name}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEraseOpen(false)} disabled={erasing}>
            Mégse
          </Button>
          <Button
            onClick={handleErase}
            color="error"
            variant="contained"
            disabled={erasing || !confirmMatches}
          >
            {erasing ? 'Törlés…' : 'Végleges törlés'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataRetention;
